import { 
  ShoppingBag, TrendingUp, Users, Globe, Shield, Zap, Target, 
  Settings, Database, Cpu, Palette, Heart, BarChart3, Package,
  Gem, Lock, Activity, Wifi, AlertTriangle, Search, RefreshCw
} from 'lucide-react';
import { ProductConfigs } from './types';

export const retailSolutionsConfigs: ProductConfigs = {
  'omnichannel-retail-platform': {
    keyBenefits: [
      {
        icon: ShoppingBag,
        title: 'Unified Commerce Experience',
        description: 'Seamless omnichannel retail platform integrating online, mobile, and in-store experiences with unified inventory',
        metrics: '360° customer experience',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'AI-Powered Personalisation',
        description: 'Advanced machine learning delivering personalised product recommendations and dynamic pricing optimisation',
        metrics: '45% conversion improvement',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Intelligent Inventory Management',
        description: 'Smart inventory optimisation with demand forecasting and automated replenishment across all channels',
        metrics: '30% inventory optimisation',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Customer Analytics Excellence',
        description: 'Deep customer behaviour analysis with lifetime value prediction and retention strategy automation',
        metrics: '65% customer retention increase',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Revenue Optimisation Engine',
        description: 'Advanced pricing strategies with margin optimisation and promotional campaign effectiveness tracking',
        metrics: '35% revenue growth',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Multi-Channel Integration',
        description: 'Seamless integration across all sales channels with unified customer profiles and consistent experiences',
        metrics: 'Complete channel unity',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Customer Data Platform',
        description: 'Unified customer data management with 360° customer profiles and behavioural analytics',
        metrics: 'Complete customer intelligence',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Cpu,
        title: 'AI-Driven Demand Forecasting',
        description: 'Machine learning powered demand prediction with seasonal analysis and trend identification capabilities',
        metrics: '95% forecast accuracy',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Heart,
        title: 'Loyalty & Rewards Engine',
        description: 'Advanced loyalty programme management with gamification, points banking, and personalised rewards',
        metrics: '90% loyalty engagement',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Workflow Automation Suite',
        description: 'Intelligent business process automation with rule-based triggers and exception management systems',
        metrics: '70% process automation',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Package,
        title: 'Supply Chain Intelligence',
        description: 'Advanced supplier management with performance monitoring, risk assessment, and procurement optimisation',
        metrics: 'Supply chain excellence',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Marketing Automation Platform',
        description: 'Sophisticated marketing campaign management with segmentation, A/B testing, and ROI tracking',
        metrics: '85% marketing efficiency',
        color: 'from-purple-500 to-indigo-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'fashion-design-production': {
    keyBenefits: [
      {
        icon: Palette,
        title: 'AI-Powered Design Intelligence',
        description: 'Advanced design assistance with trend prediction, colour palette generation, and style recommendation algorithms',
        metrics: '70% design efficiency',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Trend Forecasting Analytics',
        description: 'Real-time fashion trend analysis with social media monitoring and predictive market intelligence',
        metrics: '95% trend accuracy',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Production Planning Optimisation',
        description: 'Intelligent production scheduling with material sourcing optimisation and quality control automation',
        metrics: '40% production efficiency',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: BarChart3,
        title: 'Cost Management Excellence',
        description: 'Advanced costing analysis with margin optimisation and profit forecasting for every design',
        metrics: '25% cost reduction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Sustainable Fashion Intelligence',
        description: 'Environmental impact tracking with sustainable material sourcing and carbon footprint optimisation',
        metrics: '50% sustainability improvement',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Collaborative Design Platform',
        description: 'Multi-designer collaboration with real-time editing, version control, and feedback management systems',
        metrics: '80% collaboration efficiency',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: '3D Design & Visualisation',
        description: 'Advanced 3D design tools with virtual sampling, fit simulation, and photorealistic rendering capabilities',
        metrics: '3D design excellence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Database,
        title: 'Material & Supplier Management',
        description: 'Comprehensive material database with supplier network management and sustainable sourcing tracking',
        metrics: 'Complete supply visibility',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Package,
        title: 'Smart Pattern Making System',
        description: 'AI-assisted pattern creation with automated grading, marker optimisation, and material usage efficiency',
        metrics: 'Pattern making mastery',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Heart,
        title: 'Brand Management Suite',
        description: 'Comprehensive brand management with style guides, brand consistency enforcement, and market positioning',
        metrics: 'Brand excellence platform',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Quality Control Automation',
        description: 'Automated quality assurance with inspection workflows, defect tracking, and supplier performance monitoring',
        metrics: '99% quality standards',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Market Launch Intelligence',
        description: 'Product launch optimisation with market timing analysis, pricing strategies, and launch campaign management',
        metrics: 'Strategic market entry',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'inventory-supply-chain': {
    keyBenefits: [
      {
        icon: Target,
        title: 'AI-Driven Supply Intelligence',
        description: 'Advanced supply chain optimisation with demand forecasting, route optimisation, and inventory balancing',
        metrics: '35% cost reduction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Logistics Analytics',
        description: 'Machine learning-powered logistics with delivery optimisation and disruption prediction capabilities',
        metrics: '95% delivery accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Global Supplier Network',
        description: 'Comprehensive supplier management with performance analytics, risk assessment, and relationship optimisation',
        metrics: 'Global supplier intelligence',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Package,
        title: 'Intelligent Inventory Optimisation',
        description: 'Smart inventory management with automated reordering, stock level optimisation, and obsolescence prevention',
        metrics: '40% inventory efficiency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Cost Analysis & Profit Optimisation',
        description: 'Advanced cost tracking with margin analysis, vendor comparison, and procurement savings identification',
        metrics: '30% procurement savings',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Quality Control Excellence',
        description: 'Comprehensive quality management with inspection workflows, defect tracking, and supplier quality monitoring',
        metrics: '99% quality assurance',
        color: 'from-red-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Supply Chain Visibility',
        description: 'End-to-end supply chain transparency with real-time tracking and automated exception management',
        metrics: 'Complete chain visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'Automated Reordering System',
        description: 'Intelligent automated purchasing with supplier selection, price optimisation, and delivery scheduling',
        metrics: 'Purchase automation',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Heart,
        title: 'Risk Management Engine',
        description: 'Advanced risk assessment with scenario planning, disruption detection, and mitigation strategies',
        metrics: 'Proactive risk management',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Collaborative Planning Platform',
        description: 'Multi-party collaboration tools with shared forecasting, capacity planning, and communication systems',
        metrics: 'Seamless collaboration',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Settings,
        title: 'Warehouse Management Integration',
        description: 'Advanced warehouse operations with location tracking, pick optimisation, and automated receiving',
        metrics: 'Warehouse excellence',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Analytics Dashboard',
        description: 'Live supply chain metrics with performance dashboards, KPI tracking, and automated reporting',
        metrics: 'Real-time intelligence',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'customer-analytics-personalization': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Advanced Retail Intelligence',
        description: 'Comprehensive retail analytics with customer behaviour analysis, sales forecasting, and performance optimisation',
        metrics: '360° retail insights',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Sales Analytics',
        description: 'AI-powered sales forecasting with seasonal analysis, trend prediction, and inventory planning support',
        metrics: '92% forecast accuracy',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Customer Journey Analytics',
        description: 'Complete customer journey mapping with touchpoint analysis and conversion optimisation insights',
        metrics: 'Customer journey mastery',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Behavioural Segmentation Engine',
        description: 'Advanced customer segmentation with behavioural analytics, preference tracking, and micro-targeting capabilities',
        metrics: '85% segmentation accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Heart,
        title: 'Personalisation Excellence',
        description: 'AI-powered personalisation with dynamic content, product recommendations, and customised user experiences',
        metrics: '60% engagement increase',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Cross-Channel Analytics',
        description: 'Unified customer analytics across all channels with attribution modelling and multi-touch analysis',
        metrics: 'Complete channel insight',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'Real-Time Analytics Engine',
        description: 'Live retail analytics with instant insights, automated reporting, and dynamic dashboard generation',
        metrics: 'Real-time retail intelligence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Database,
        title: 'Data Integration Platform',
        description: 'Unified retail data platform integrating POS, inventory, CRM, and external market data sources',
        metrics: 'Complete data integration',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'A/B Testing & Optimisation',
        description: 'Advanced experimentation platform with multivariate testing, statistical analysis, and conversion optimisation',
        metrics: '45% conversion improvement',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: AlertTriangle,
        title: 'Churn Prediction & Prevention',
        description: 'AI-powered churn analysis with early warning systems and automated retention campaign triggers',
        metrics: '70% churn reduction',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Package,
        title: 'Product Performance Analytics',
        description: 'Comprehensive product analysis with performance tracking, margin analysis, and inventory optimisation insights',
        metrics: 'Product intelligence mastery',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Automated Insight Generation',
        description: 'AI-powered insight discovery with automated anomaly detection and actionable recommendation engine',
        metrics: 'Intelligent automation',
        color: 'from-yellow-500 to-amber-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'fashion-design-software': {
    keyBenefits: [
      {
        icon: Palette,
        title: 'Creative Design Excellence',
        description: 'Professional fashion design tools with advanced pattern creation, 3D visualization, and fabric simulation capabilities',
        metrics: '90% design productivity',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Cpu,
        title: '3D Design & Visualization',
        description: 'Cutting-edge 3D design environment with realistic fabric simulation, virtual fitting, and photorealistic rendering',
        metrics: '3D design mastery',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Pattern Making Precision',
        description: 'Advanced pattern design tools with automated grading, marker optimization, and technical drawing capabilities',
        metrics: '85% pattern accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Collection Management System',
        description: 'Comprehensive collection organization with version control, collaboration tools, and design history tracking',
        metrics: 'Complete design control',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Trend Integration Platform',
        description: 'Real-time fashion trend analysis with color forecasting, style prediction, and market intelligence integration',
        metrics: '95% trend accuracy',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Export & Manufacturing Ready',
        description: 'Professional export capabilities with technical specifications, manufacturing documents, and production planning',
        metrics: 'Manufacturing excellence',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Heart,
        title: 'Fabric Simulation Engine',
        description: 'Advanced fabric physics with drape simulation, texture mapping, and material behavior modeling for realistic visualization',
        metrics: 'Realistic fabric physics',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Collaborative Design Platform',
        description: 'Multi-designer collaboration with real-time editing, version control, and team communication systems',
        metrics: '80% collaboration efficiency',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Size Grading Automation',
        description: 'Intelligent size grading with automated pattern adjustments, proportion scaling, and fit optimization',
        metrics: 'Perfect size scaling',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Settings,
        title: 'Color Management Suite',
        description: 'Professional color management with Pantone integration, color matching, and print optimization',
        metrics: 'Color perfection',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Package,
        title: 'Technical Drawing Generator',
        description: 'Automated technical drawing creation with specification sheets, construction details, and manufacturing guides',
        metrics: 'Technical documentation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Design-to-Production Pipeline',
        description: 'Seamless workflow from design to production with automated file conversion and manufacturing integration',
        metrics: 'Production ready output',
        color: 'from-blue-500 to-indigo-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'jewelry-store-management': {
    keyBenefits: [
      {
        icon: Gem,
        title: 'Luxury Inventory Excellence',
        description: 'Comprehensive jewelry inventory management with detailed cataloging, gemstone tracking, and valuation systems',
        metrics: '99% inventory accuracy',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: BarChart3,
        title: 'Appraisal Management System',
        description: 'Professional appraisal tracking with certification management, value monitoring, and insurance integration',
        metrics: 'Professional appraisal control',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Customer Relationship Excellence',
        description: 'Sophisticated customer database with purchase history, preferences, and personalized service management',
        metrics: '95% customer satisfaction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Shield,
        title: 'Advanced Security Features',
        description: 'Comprehensive security management with access control, surveillance integration, and theft prevention systems',
        metrics: 'Maximum security protection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Settings,
        title: 'Repair & Service Tracking',
        description: 'Complete repair workflow management with service scheduling, progress tracking, and customer communication',
        metrics: '90% service efficiency',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: TrendingUp,
        title: 'Sales Analytics Intelligence',
        description: 'Advanced sales analytics with trend analysis, seasonal patterns, and luxury market intelligence',
        metrics: '35% sales optimization',
        color: 'from-orange-500 to-red-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Certificate Management Suite',
        description: 'Digital certificate tracking with authenticity verification, insurance documentation, and compliance management',
        metrics: 'Certificate authenticity control',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Insurance Integration Platform',
        description: 'Seamless insurance provider integration with automated claims processing and valuation updates',
        metrics: 'Insurance excellence',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Heart,
        title: 'Custom Design Management',
        description: 'Complete custom jewelry workflow with design approval, production tracking, and customer collaboration',
        metrics: 'Custom design mastery',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Target,
        title: 'Vendor & Supplier Relations',
        description: 'Advanced supplier management with performance tracking, quality assessment, and procurement optimization',
        metrics: 'Supplier excellence',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Package,
        title: 'Layaway & Payment Plans',
        description: 'Flexible payment management with layaway tracking, installment plans, and automated payment processing',
        metrics: 'Payment flexibility',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Cpu,
        title: 'Gemstone Authentication System',
        description: 'Advanced gemstone verification with digital imaging, authenticity tracking, and quality grading integration',
        metrics: 'Gemstone authenticity',
        color: 'from-amber-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated audit trails and regulatory reporting',
        metrics: 'Enterprise compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat intelligence, anomaly detection, and incident response automation',
        metrics: '24/7 threat protection',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime with auto-scaling security systems and distributed denial-of-service protection',
        metrics: 'Maximum availability',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Wifi,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layered security with intelligent threat filtering and adaptive defence mechanisms',
        metrics: 'Proactive threat defence',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication',
        metrics: 'Zero-trust security',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Search,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with correlation analysis, threat hunting, and automated incident response',
        metrics: 'Intelligent security ops',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Advanced data protection with encrypted backups, point-in-time recovery, and data loss prevention',
        metrics: 'Complete data protection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and lateral movement prevention',
        metrics: 'Network security isolation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and security posture management',
        metrics: 'Continuous security validation',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  }
};